import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../lib/api';
import { useChat } from '../hooks/useChat';
import useChatMode from '../hooks/useChatMode';
import useSpeechOutput from '../hooks/useSpeechOutput';
import { canUseVoice } from '../lib/vad';
import ChatWindow from '../components/chat/ChatWindow';
import RelatedIdeas from '../components/chat/RelatedIdeas';
import PointsDrawer from '../components/chat/PointsDrawer';
import VoiceSession from '../components/chat/VoiceSession';
import PointsPanel from '../components/points/PointsPanel';
import toast from 'react-hot-toast';

export default function ChatPage() {
  const { id: ideaId, conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const {
    messages,
    points,
    streaming,
    extractingPoints,
    sendMessage,
    loadMessages,
    loadPoints,
    setMessages,
  } = useChat(conversationId || conversation?.id, ideaId);

  const [mode, setMode] = useChatMode();
  // Only used here to unlock speech inside a tap; the surface owns speaking.
  const { unlock: unlockSpeech } = useSpeechOutput();
  // Whether the full-screen hands-free surface is open.
  const [voiceOpen, setVoiceOpen] = useState(false);

  // Voice is record-then-transcribe now, so support means MediaRecorder +
  // AudioContext + a secure context, not the Web Speech API.
  const voiceSupported = canUseVoice();

  useEffect(() => {
    loadPoints();
  }, [loadPoints]);

  // A saved voice mode is meaningless in a browser without speech.
  useEffect(() => {
    if (!voiceSupported && mode !== 'type') setMode('type');
  }, [voiceSupported, mode, setMode]);

  const startVoice = useCallback(() => {
    // Unlock speech synthesis inside this tap (mobile autoplay blocks any
    // speak() that never followed a gesture), then open the hands-free
    // surface, which owns its own listen/think/speak loop.
    unlockSpeech();
    setVoiceOpen(true);
  }, [unlockSpeech]);

  useEffect(() => {
    const init = async () => {
      try {
        const res = conversationId
          ? await api.get(`/conversations/${conversationId}/`)
          : await api.post(`/ideas/${ideaId}/conversations/start/`, {
              persona: 'adaptive',
            });
        setConversation(res.data);
        setMessages(res.data.messages || []);
        if (!conversationId) {
          // Update URL without navigation
          window.history.replaceState(
            null, '', `/ideas/${ideaId}/chat/${res.data.id}`
          );
        }
      } catch (err) {
        toast.error('Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [ideaId, conversationId, setMessages]);

  const handlePersonaChange = (persona) => {
    setConversation((prev) => ({ ...prev, persona }));
  };

  if (loading) return <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading...</div>;

  return (
    <div className="h-full">
      <div className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <button onClick={() => navigate(`/ideas/${ideaId}`)} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          <ArrowLeftIcon className="w-5 h-5" />
        </button>
        <h2 className="font-medium text-gray-900 dark:text-white truncate">
          {conversation?.title || 'Chat'}
        </h2>
      </div>
      <div className="flex h-[calc(100vh-3.5rem)]">
        {/* min-w-0: without it the flex item refuses to shrink below its
            content's intrinsic width (the persona row), overflowing the
            viewport on a phone and pushing the send button off-screen. */}
        <div className="flex-1 min-w-0">
          <ChatWindow
            messages={messages}
            streaming={streaming}
            onSend={sendMessage}
            conversationId={conversation?.id}
            persona={conversation?.persona || 'adaptive'}
            onPersonaChange={handlePersonaChange}
            mode={mode}
            onModeChange={setMode}
            voiceSupported={voiceSupported}
            onVoiceGesture={unlockSpeech}
            onStartVoice={startVoice}
          />
        </div>
        <div className="hidden lg:block w-72 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          <div className="p-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Points {points.length > 0 && `(${points.length})`}
            </h3>
            <PointsPanel
              points={points}
              emptyMessage="Points appear here as you talk."
            />
            {extractingPoints && (
              <p className="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">
                Pulling out new points…
              </p>
            )}
          </div>
          <RelatedIdeas ideaId={ideaId} />
        </div>
      </div>

      {/* Sibling of the chat column so the sheet isn't clipped by overflow. */}
      <PointsDrawer points={points} extracting={extractingPoints} />

      {voiceOpen && (conversation?.id || conversationId) && (
        <VoiceSession
          conversationId={conversation?.id || conversationId}
          mode={mode}
          onModeChange={setMode}
          onClose={() => {
            setVoiceOpen(false);
            // The surface appended turns server-side; refresh the chat + points.
            loadMessages();
            loadPoints();
          }}
        />
      )}
    </div>
  );
}
